import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExoDialog from '../../../components/exo/ExoDialog';
import { PhaseWrapper } from './PhaseWrapper';
import { ProjectListItem } from './ProjectListItem';
import ProjectTimeCard from '../ProjectTimeCard';
import { useTimeTracking } from '../../../components/time-tracking/AppTimeProvider';
import { useConfig } from '../../../api/api';

function clientNameOf(project) {
  if (!project.client) return '';
  if (project.clientType === 'company') return project.client.name || '';
  return `${project.client.firstName || ''} ${project.client.lastName || ''}`;
}

function matchesSearch(project, query) {
  if (!query) return true;
  const haystack = [
    project.name,
    project.nr,
    project.externalNr,
    clientNameOf(project),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export const ProjectList = ({
  projects,
  viewMode = 'grid',
  searchQuery = '',
  showAll = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [selectedProject, setSelectedProject] = useState(null);
  const { clockIn, activeProject } = useTimeTracking();
  const config = useConfig();
  const requireWorkPackage = Boolean(
    Number(config?.timesheet?.requireWorkPackageForTimeTracking)
  );

  function hasSelectableWorkPackages(project) {
    return (project.phases || []).some((phase) =>
      phase.workPackages.some(
        (workPackage) =>
          (workPackage.status === 0 || workPackage.status === 2) &&
          (!requireWorkPackage || Boolean(workPackage.isTimeTrackable))
      )
    );
  }

  // Why the user cannot clock into the project, or null if they can.
  // Prefers the backend flag (sent for includeNonTrackable requests) and
  // falls back to the same rules evaluated on the client.
  function blockedReasonOf(project) {
    if (project.isTimeTrackingAllowed === false)
      return project.timeTrackingBlockedReason || 'noWorkPackage';
    if (project.isTimeTrackingAllowed === true) return null;
    if (project.status !== 0) return 'archived';
    if (requireWorkPackage && !hasSelectableWorkPackages(project))
      return 'noWorkPackage';
    return null;
  }

  const reasonLabels = {
    archived: t('Project is archived'),
    noWorkPackage: t('No work package in this project is assigned to you'),
  };

  function handleProjectSelection(project) {
    if (hasSelectableWorkPackages(project)) {
      setSelectedProject(project);
    } else if (!requireWorkPackage) {
      clockIn(project);
    }
  }

  const query = searchQuery.trim().toLowerCase();

  const shownProjects = useMemo(() => {
    const withReason = projects.map((project) => ({
      project,
      blockedReason: blockedReasonOf(project),
    }));
    const visible = showAll
      ? withReason
      : withReason.filter((entry) => !entry.blockedReason);
    const searched = visible.filter((entry) =>
      matchesSearch(entry.project, query)
    );
    // keep the backend ordering, but list clock-in-able projects first
    return [
      ...searched.filter((entry) => !entry.blockedReason),
      ...searched.filter((entry) => entry.blockedReason),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, showAll, query, requireWorkPackage]);

  const isList = viewMode === 'list';

  return (
    <Box
      className={
        isList
          ? 'flex flex-col gap-2 h-full'
          : 'flex justify-around sm:justify-start flex-wrap gap-2 h-full'
      }
      sx={{
        overflowY: activeProject ? 'hidden' : 'auto',
        height: activeProject ? '600px' : 'auto',
      }}
    >
      {shownProjects.map(({ project, blockedReason }) =>
        isList ? (
          <ProjectListItem
            key={project.id}
            project={project}
            onClick={handleProjectSelection}
            disabled={Boolean(blockedReason)}
            disabledReason={reasonLabels[blockedReason] || ''}
          />
        ) : (
          <ProjectTimeCard
            key={project.id}
            project={project}
            onClick={handleProjectSelection}
            disabled={Boolean(blockedReason)}
            disabledReason={reasonLabels[blockedReason] || ''}
          />
        )
      )}
      {shownProjects.length === 0 && (
        <Box
          className="w-full flex items-center justify-center rounded-xl backdrop-blur-lg py-8 px-4"
          sx={{ backgroundColor: colors.glass }}
        >
          <Typography variant="h5" className="text-center">
            {t('No projects found')}
          </Typography>
        </Box>
      )}
      <ExoDialog
        open={Boolean(selectedProject)}
        onClose={() => setSelectedProject(null)}
      >
        {selectedProject && (
          <Box className="h-full flex flex-col overflow-y-auto">
            <Typography variant="h2" className="px-2 pb-4">
              {selectedProject.name}
            </Typography>
            <Box className="flex flex-col gap-5">
              {selectedProject.phases.map(
                (phase) =>
                  phase.workPackages.length > 0 && (
                    <PhaseWrapper
                      key={phase.id}
                      phase={phase}
                      project={selectedProject}
                      onDeselect={() => setSelectedProject(null)}
                    />
                  )
              )}
            </Box>
          </Box>
        )}
      </ExoDialog>
    </Box>
  );
};
